import { makeStyles, Theme } from '@material-ui/core'
import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import ScheduleIcon from '@material-ui/icons/Schedule'

import { JobStatus, WorkflowStatus } from '../../../../api'

export type AnyStatus = WorkflowStatus | JobStatus

type Tone = 'success' | 'failure' | 'running' | 'hold' | 'canceled' | 'neutral'

const TONE_BY_STATUS: Record<string, Tone> = {
  success: 'success',
  fixed: 'success',
  running: 'running',
  failing: 'running',
  on_hold: 'hold',
  blocked: 'hold',
  queued: 'hold',
  not_running: 'hold',
  scheduled: 'hold',
  failed: 'failure',
  error: 'failure',
  infrastructure_fail: 'failure',
  timedout: 'failure',
  unauthorized: 'failure',
  canceled: 'canceled',
  'terminated-unknown': 'canceled',
  retried: 'canceled',
  not_run: 'neutral',
}

const READABLE_LABELS: Record<string, string> = {
  success: 'Success',
  running: 'Running',
  failing: 'Failing',
  failed: 'Failed',
  error: 'Error',
  on_hold: 'Needs Approval',
  blocked: 'Blocked',
  queued: 'Queued',
  not_running: 'Not Running',
  not_run: 'Not Run',
  scheduled: 'Scheduled',
  canceled: 'Canceled',
  retried: 'Retried',
  fixed: 'Fixed',
  infrastructure_fail: 'Infra Fail',
  timedout: 'Timed Out',
  unauthorized: 'Unauthorized',
  'terminated-unknown': 'Terminated',
}

const useStyles = makeStyles<Theme, { tone: Tone; size: 'sm' | 'md' }>(theme => ({
  badge: ({ tone, size }) => {
    const palette = (() => {
      switch (tone) {
        case 'success':
          return { bg: 'rgba(56, 161, 105, 0.18)', fg: '#48bb78' }
        case 'running':
          return { bg: 'rgba(56, 130, 196, 0.18)', fg: '#63b3ed' }
        case 'hold':
          return { bg: 'rgba(159, 122, 234, 0.22)', fg: '#b794f4' }
        case 'failure':
          return { bg: 'rgba(229, 62, 62, 0.2)', fg: '#fc8181' }
        case 'canceled':
          return { bg: 'rgba(160, 174, 192, 0.18)', fg: '#cbd5e0' }
        case 'neutral':
        default:
          return { bg: 'rgba(160, 174, 192, 0.12)', fg: theme.palette.text.secondary }
      }
    })()

    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      paddingLeft: size === 'sm' ? theme.spacing(0.75) : theme.spacing(1.25),
      paddingRight: size === 'sm' ? theme.spacing(1.25) : theme.spacing(1.5),
      paddingTop: size === 'sm' ? 2 : 4,
      paddingBottom: size === 'sm' ? 2 : 4,
      borderRadius: 16,
      fontSize: size === 'sm' ? 12 : 13,
      fontWeight: 600,
      lineHeight: 1.2,
      color: palette.fg,
      backgroundColor: palette.bg,
      whiteSpace: 'nowrap',
    }
  },
  icon: ({ size }) => ({
    fontSize: size === 'sm' ? 14 : 16,
  }),
}))

const iconForTone = (tone: Tone) => {
  switch (tone) {
    case 'success':
      return CheckCircleIcon
    case 'failure':
      return ErrorIcon
    case 'running':
      return ScheduleIcon
    case 'hold':
      return PauseCircleFilledIcon
    case 'canceled':
      return CancelIcon
    case 'neutral':
    default:
      return RemoveCircleIcon
  }
}

export const StatusBadge = ({ status, size = 'md' }: { status: AnyStatus; size?: 'sm' | 'md' }) => {
  const tone: Tone = TONE_BY_STATUS[status] ?? 'neutral'
  const classes = useStyles({ tone, size })
  const Icon = iconForTone(tone)
  const label = READABLE_LABELS[status] ?? status

  return (
    <span className={classes.badge}>
      <Icon className={classes.icon} />
      {label}
    </span>
  )
}
